import React from 'react';

import PropTypes from 'prop-types';

import VerifiedLabel from '../components/VerifiedIcon';
import {Translate, translate} from 'views/module';
import {Box, Stack, Typography} from 'views/components';
import {WorkspacePremiumOutlined, VerifiedUserOutlined, DescriptionOutlined} from 'views/icons';

import moment from 'moment';

const CredentialsSection = (props) => {
  const {questions} = props;
  const {
    firstName,
    certificates = [],
  } = questions;

  return (
    <Stack alignItems="flex-start" width="100%" spacing={2} sx={{}}>
      {/* Section Title */}
      <Stack>
        <Typography variant="h5" fontWeight={700} sx={{mb: 1}}>
          <Translate expression='caregiverProfile.Credentials.Title'/>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{}}>
          <Translate
            expression='caregiverProfile.Credentials.Subtitle'
            substitute={{name: firstName}}
          />
        </Typography>
      </Stack>
      {/* Pre-Vetted Banner */}
      <Stack
        direction="row" alignItems="center" spacing={2} width="100%"
        sx={{
          p: 2,
          borderRadius: 4,
          bgcolor: '#F3FBF6',
          boxSizing: 'border-box',
          border: '1px solid',
          borderColor: 'grey.300',
          m: 0,
        }}>
        <Box
          sx={{
            bgcolor: '#E6FAF0',
            borderRadius: '50%',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <VerifiedUserOutlined sx={{color: 'success.main', fontSize: 'large'}} />
        </Box>
        <Stack>
          <Typography variant="h6" color="#1B5E20" fontWeight="bold">
            <Translate
              expression='caregiverProfile.Credentials.PreVettedTitle'
              substitute={{name: firstName}}
            />
          </Typography>
          <Typography variant="body2" color="#2E7D32 ">
            <Translate
              expression='caregiverProfile.Credentials.PreVettedSubtitle'
              substitute={{name: firstName}}
            />
          </Typography>
        </Stack>
      </Stack>
      {/* Qualifications Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'},
          gap: 2,
        }}
      >
        {/* Caregiver Qualifications Card */}
        {
          certificates.map((c) => (
            <Stack
              key={c.type} direction="row" alignItems="flex-start" spacing={2}
              sx={{
                'p': 2,
                'borderRadius': 4,
                'bgcolor': 'white',
                'width': '100%',
                'boxSizing': 'border-box',
                'border': '1px solid',
                'borderColor': 'grey.300',
                'm': 0,
                'transition': 'all 0.3s ease',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {/* Icon */}
              <Box sx={{bgcolor: '#F5F6FF', borderRadius: 2, p: 1, display: 'flex', alignItems: 'center', mr: 2}}>
                <WorkspacePremiumOutlined sx={{color: '#6C63FF', fontSize: {xs: 20, sm: 24, md: 28, lg: 32}}} />
              </Box>
              {/* Title, Description & Label */}
              <Stack spacing={1} alignContent="flex-start">
                <Typography variant="subtitle2" fontWeight="bold">
                  <Translate expression={`caregiverProfile.Credentials.CaregiverInfo.${c.type}.Type`} />
                </Typography>
                <Typography variant="body2" color="grey.700">
                  {
                    translate(`caregiverProfile.Credentials.CaregiverInfo.${c.type}.Place`,
                        {orgName: c.issuingOrganization.trim() || translate('caregiverProfile.Credentials.OrgNameDefault')}) +
              (c.city.trim() ? `— ${c.city.trim()}` : '') +
              (c.dateObtained.trim() ? ` (${moment(c.dateObtained).format('YYYY')})` : '')
                  }
                </Typography>
                <Box
                  sx={{
                    width: 'fit-content',
                    textAlign: 'center',
                    bgcolor: '#dbeafe',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'primary.light',
                    px: 1,
                  }}
                >
                  <Typography variant="caption" fontWeight="bold" color={'#3053b7'}>
                    <Translate expression={`caregiverProfile.Credentials.CaregiverInfo.${c.type}.Label`}/>
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{flex: 1}} />
              {/* Verified Icon */}
              <VerifiedLabel/>
            </Stack>
          ))
        }
        {/* Background Check Card */}
        <Stack
          direction="row" alignItems="flex-start" spacing={2}
          sx={{
            'p': 2,
            'borderRadius': 4,
            'bgcolor': 'white',
            'width': '100%',
            'boxSizing': 'border-box',
            'border': '1px solid',
            'borderColor': 'grey.300',
            'm': 0,
            'transition': 'all 0.3s ease',
            '&:hover': {
              boxShadow: 4,
              transform: 'translateY(-2px)',
            },
          }}
        >
          {/* Icon */}
          <Box sx={{bgcolor: '#F5F6FF', borderRadius: 2, p: 1, display: 'flex', alignItems: 'center', mr: 2}}>
            <VerifiedUserOutlined sx={{color: '#6C63FF', fontSize: {xs: 20, sm: 24, md: 28, lg: 32}}} />
          </Box>
          {/* Title, Description & Label */}
          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight="bold">
              <Translate expression={`caregiverProfile.Credentials.BgCheckTitle`} />
            </Typography>
            <Typography variant="body2" color="grey.700">
              <Translate
                expression={`caregiverProfile.Credentials.BgCheckSubtitle`}
                substitute={{orgNameDefault: translate('caregiverProfile.Credentials.OrgNameDefault')}}
              />
            </Typography>
            <Box
              sx={{
                textAlign: 'center',
                bgcolor: '#dcfce7',
                borderRadius: 2,
                border: 1,
                borderColor: 'primary.light',
                px: 1,
                width: 'fit-content',
              }}>
              <Typography variant="caption" fontWeight="bold" color='#166534'>
                <Translate expression={`caregiverProfile.Credentials.BgCheckLabel`}/>
              </Typography>
            </Box>
          </Stack>
          {/* Verified Icon */}
          <Box sx={{flex: 1}} />
          <VerifiedLabel/>
        </Stack>
        {/* Liability Insurance Card */}
        <Stack
          direction="row" alignItems="flex-start" spacing={2}
          sx={{
            'p': 2,
            'borderRadius': 4,
            'bgcolor': 'white',
            'width': '100%',
            'boxSizing': 'border-box',
            'border': '1px solid',
            'borderColor': 'grey.300',
            'm': 0,
            'transition': 'all 0.3s ease',
            '&:hover': {
              boxShadow: 4,
              transform: 'translateY(-2px)',
            },
          }}
        >
          {/* Icon */}
          <Box sx={{bgcolor: '#F5F6FF', borderRadius: 2, p: 1, display: 'flex', alignItems: 'center', mr: 2}}>
            <DescriptionOutlined sx={{color: '#6C63FF', fontSize: {xs: 20, sm: 24, md: 28, lg: 32}}} />
          </Box>
          {/* Title, Description & Label */}
          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight="bold">
              <Translate expression={`caregiverProfile.Credentials.LiabilityInsuranceTitle`} />
            </Typography>
            <Typography variant="body2" color="grey.700">
              <Translate
                expression={`caregiverProfile.Credentials.LiabilityInsuranceSubtitle`}
                substitute={{orgNameDefault: translate('caregiverProfile.Credentials.OrgNameDefault')}}
              />
            </Typography>
            <Box
              sx={{
                textAlign: 'center',
                bgcolor: '#f3e8ff',
                borderRadius: 2,
                border: 1,
                borderColor: 'primary.light',
                px: 1,
                width: 'fit-content',
              }}>
              <Typography variant="caption" fontWeight="bold" color='#ac47a8'>
                <Translate expression={`caregiverProfile.Credentials.LiabilityInsuranceLabel`}/>
              </Typography>
            </Box>
          </Stack>
          {/* Verified Icon */}
          <Box sx={{flex: 1}} />
          <VerifiedLabel/>
        </Stack>
      </Box>

    </Stack>
  );
};

CredentialsSection.propTypes = {
  questions: PropTypes.object.isRequired,
};

export default CredentialsSection;
